import React, { useState } from 'react';
import { Search, Filter, X, Calendar, Save, FolderOpen } from 'lucide-react';
import { Button } from './Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { toast } from 'sonner';
import { ExportDropdown } from './ExportDropdown';
import { SyncButton } from './SyncButton';
import { StatusBadge } from './StatusBadge';
import { UrgencyBadge } from './UrgencyBadge';
export type FilterField = {
  key: string;
  label: string;
  type: 'search' | 'select' | 'multiSelect' | 'datePreset' | 'dateRange' | 'numberRange' | 'statusBadge' | 'urgencyBadge';
  options?: {
    label: string;
    value: string;
  }[];
  placeholder?: string;
};
export interface FilterBarProps {
  fields: FilterField[];
  values: Record<string, any>;
  onChange: (key: string, value: any) => void;
  onApply: () => void;
  onReset: () => void;
  resultCount?: number;
  // For export
  exportData?: any[];
  exportColumns?: {
    key: string;
    label: string;
  }[];
  exportFilename?: string;
}
const DATE_PRESETS = ['Hari ini', 'Kemarin', '7 hari terakhir', '30 hari terakhir', 'Bulan ini', 'Triwulan ini', 'Tahun ini', 'Rentang tanggal custom'];
export function FilterBar({
  fields,
  values,
  onChange,
  onApply,
  onReset,
  resultCount,
  exportData,
  exportColumns,
  exportFilename
}: FilterBarProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const handleSavePreset = () => {
    toast.success('Preset filter berhasil disimpan.');
  };
  const handleLoadPreset = () => {
    toast.success('Preset filter berhasil dimuat.');
  };
  const removeFilter = (key: string) => {
    onChange(key, undefined);
    // Also clear range keys if they exist
    onChange(`${key}Start`, undefined);
    onChange(`${key}End`, undefined);
    onChange(`${key}Min`, undefined);
    onChange(`${key}Max`, undefined);
    // Auto apply when removing a chip
    setTimeout(onApply, 50);
  };
  // Generate active filter chips
  const activeFilters = Object.entries(values).filter(([k, v]) => v !== undefined && v !== '' && v !== 'all' && !k.endsWith('Start') && !k.endsWith('End') && !k.endsWith('Min') && !k.endsWith('Max'));
  // Also catch range filters that might not have a base key
  fields.forEach(f => {
    if (f.type === 'dateRange') {
      if (values[`${f.key}Start`] || values[`${f.key}End`]) {
        if (!activeFilters.find(([k]) => k === f.key)) {
          activeFilters.push([f.key, `${values[`${f.key}Start`] || '...'} - ${values[`${f.key}End`] || '...'}`]);
        }
      }
    }
    if (f.type === 'numberRange') {
      if (values[`${f.key}Min`] || values[`${f.key}Max`]) {
        if (!activeFilters.find(([k]) => k === f.key)) {
          activeFilters.push([f.key, `${values[`${f.key}Min`] || 'Min'} - ${values[`${f.key}Max`] || 'Max'}`]);
        }
      }
    }
  });
  return <div className="glass-panel border-border/50 p-4 rounded-md flex flex-col gap-4 mb-4 animate-in fade-in slide-in-from-top-2" data-id="element-182">
      <div className="flex items-center justify-between" data-id="element-183">
        <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground uppercase tracking-widest" data-id="element-184">
          <Filter className="w-4 h-4 text-primary" data-id="element-185" />
          Filter Data Lanjutan
        </div>
        <div className="flex items-center gap-2" data-id="element-186">
          <Button variant="ghost" size="sm" onClick={handleLoadPreset} className="h-7 text-[10px] font-mono hover:text-primary" data-id="element-187">
            <FolderOpen className="w-3 h-3 mr-1.5" data-id="element-188" /> Muat
          </Button>
          <Button variant="ghost" size="sm" onClick={handleSavePreset} className="h-7 text-[10px] font-mono hover:text-primary" data-id="element-189">
            <Save className="w-3 h-3 mr-1.5" data-id="element-190" /> Simpan
          </Button>
          <div className="w-px h-4 bg-border/50 mx-1" data-id="element-191"></div>
          <Button variant="ghost" size="sm" onClick={() => setIsExpanded(!isExpanded)} className="h-7 text-[10px] font-mono" data-id="element-192">
            {isExpanded ? 'Sembunyikan' : 'Tampilkan'}
          </Button>
        </div>
      </div>

      {isExpanded && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-193">
          {fields.map(field => {
        if (field.type === 'search') {
          return <div key={field.key} className="space-y-1.5" data-id="element-194">
                  <label className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider" data-id="element-195">
                    {field.label}
                  </label>
                  <div className="relative" data-id="element-196">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-id="element-197" />
                    <input type="text" placeholder={field.placeholder || `Cari...`} value={values[field.key] || ''} onChange={e => onChange(field.key, e.target.value)} className="w-full pl-9 pr-4 py-1.5 bg-background/50 border border-border/50 rounded text-xs font-mono focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all" data-id="element-198" />
                  </div>
                </div>;
        }
        if (field.type === 'select') {
          return <div key={field.key} className="space-y-1.5" data-id="element-199">
                  <label className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider" data-id="element-200">
                    {field.label}
                  </label>
                  <Select value={values[field.key] || ''} onValueChange={val => onChange(field.key, val)} data-id="element-201">
                    <SelectTrigger className="h-8 bg-background/50 border-border/50 text-xs font-mono" data-id="element-202">
                      <SelectValue placeholder={field.placeholder || 'Semua'} data-id="element-203" />
                    </SelectTrigger>
                    <SelectContent data-id="element-204">
                      <SelectItem value="all" data-id="element-205">Semua</SelectItem>
                      {field.options?.map(opt => <SelectItem key={opt.value} value={opt.value} data-id="element-206">
                          {opt.label}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>;
        }
        if (field.type === 'datePreset') {
          const isCustom = values[field.key] === 'Rentang tanggal custom';
          return <div key={field.key} className={`space-y-1.5 ${isCustom ? 'col-span-1 md:col-span-2' : ''}`} data-id="element-207">
                  <label className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider" data-id="element-208">
                    {field.label}
                  </label>
                  <div className="flex gap-2" data-id="element-209">
                    <Select value={values[field.key] || ''} onValueChange={val => onChange(field.key, val)} data-id="element-210">
                      <SelectTrigger className="h-8 bg-background/50 border-border/50 text-xs font-mono flex-1" data-id="element-211">
                        <SelectValue placeholder="Pilih Waktu" data-id="element-212" />
                      </SelectTrigger>
                      <SelectContent data-id="element-213">
                        <SelectItem value="all" data-id="element-214">Semua Waktu</SelectItem>
                        {DATE_PRESETS.map(opt => <SelectItem key={opt} value={opt} data-id="element-215">
                            {opt}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>

                    {isCustom && <div className="flex items-center gap-2 flex-1" data-id="element-216">
                        <input type="date" value={values[`${field.key}Start`] || ''} onChange={e => onChange(`${field.key}Start`, e.target.value)} className="w-full px-2 py-1.5 bg-background/50 border border-border/50 rounded text-[10px] font-mono focus:outline-none focus:border-primary/50 text-muted-foreground h-8" data-id="element-217" />
                        <span className="text-muted-foreground text-xs" data-id="element-218">-</span>
                        <input type="date" value={values[`${field.key}End`] || ''} onChange={e => onChange(`${field.key}End`, e.target.value)} className="w-full px-2 py-1.5 bg-background/50 border border-border/50 rounded text-[10px] font-mono focus:outline-none focus:border-primary/50 text-muted-foreground h-8" data-id="element-219" />
                      </div>}
                  </div>
                </div>;
        }
        if (field.type === 'dateRange') {
          return <div key={field.key} className="space-y-1.5 col-span-1 md:col-span-2" data-id="element-220">
                  <label className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider" data-id="element-221">
                    {field.label}
                  </label>
                  <div className="flex items-center gap-2" data-id="element-222">
                    <div className="relative flex-1" data-id="element-223">
                      <Calendar className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" data-id="element-224" />
                      <input type="date" value={values[`${field.key}Start`] || ''} onChange={e => onChange(`${field.key}Start`, e.target.value)} className="w-full pl-7 pr-2 py-1.5 bg-background/50 border border-border/50 rounded text-[10px] font-mono focus:outline-none focus:border-primary/50 transition-all text-muted-foreground h-8" data-id="element-225" />
                    </div>
                    <span className="text-muted-foreground text-xs" data-id="element-226">-</span>
                    <div className="relative flex-1" data-id="element-227">
                      <Calendar className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" data-id="element-228" />
                      <input type="date" value={values[`${field.key}End`] || ''} onChange={e => onChange(`${field.key}End`, e.target.value)} className="w-full pl-7 pr-2 py-1.5 bg-background/50 border border-border/50 rounded text-[10px] font-mono focus:outline-none focus:border-primary/50 transition-all text-muted-foreground h-8" data-id="element-229" />
                    </div>
                  </div>
                </div>;
        }
        if (field.type === 'numberRange') {
          return <div key={field.key} className="space-y-1.5" data-id="element-230">
                  <label className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider" data-id="element-231">
                    {field.label}
                  </label>
                  <div className="flex items-center gap-2" data-id="element-232">
                    <input type="number" placeholder="Min" value={values[`${field.key}Min`] || ''} onChange={e => onChange(`${field.key}Min`, e.target.value)} className="w-full px-2 py-1.5 bg-background/50 border border-border/50 rounded text-xs font-mono focus:outline-none focus:border-primary/50 h-8" data-id="element-233" />
                    <span className="text-muted-foreground text-xs" data-id="element-234">-</span>
                    <input type="number" placeholder="Max" value={values[`${field.key}Max`] || ''} onChange={e => onChange(`${field.key}Max`, e.target.value)} className="w-full px-2 py-1.5 bg-background/50 border border-border/50 rounded text-xs font-mono focus:outline-none focus:border-primary/50 h-8" data-id="element-235" />
                  </div>
                </div>;
        }
        if (field.type === 'statusBadge') {
          return <div key={field.key} className="space-y-1.5 col-span-1 md:col-span-2" data-id="element-236">
                  <label className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider" data-id="element-237">
                    {field.label}
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-238">
                    {field.options?.map(opt => {
                const isSelected = values[field.key] === opt.value;
                return <div key={opt.value} onClick={() => onChange(field.key, isSelected ? undefined : opt.value)} className={`cursor-pointer transition-all ${isSelected ? 'ring-2 ring-primary ring-offset-1 ring-offset-background rounded-full' : 'opacity-70 hover:opacity-100'}`} data-id="element-239">
                          <StatusBadge status={opt.value as any} data-id="element-240" />
                        </div>;
              })}
                  </div>
                </div>;
        }
        if (field.type === 'urgencyBadge') {
          return <div key={field.key} className="space-y-1.5 col-span-1 md:col-span-2" data-id="element-241">
                  <label className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider" data-id="element-242">
                    {field.label}
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-243">
                    {field.options?.map(opt => {
                const isSelected = values[field.key] === opt.value;
                return <div key={opt.value} onClick={() => onChange(field.key, isSelected ? undefined : opt.value)} className={`cursor-pointer transition-all ${isSelected ? 'ring-2 ring-primary ring-offset-1 ring-offset-background rounded-full' : 'opacity-70 hover:opacity-100'}`} data-id="element-244">
                          <UrgencyBadge urgency={opt.value as any} data-id="element-245" />
                        </div>;
              })}
                  </div>
                </div>;
        }
        return null;
      })}
        </div>}

      {/* Filter Chips & Actions Toolbar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-3 border-t border-border/30" data-id="element-246">
        <div className="flex flex-wrap items-center gap-2 flex-1" data-id="element-247">
          {activeFilters.length > 0 ? <>
              <span className="text-[10px] font-mono text-muted-foreground mr-1" data-id="element-248">
                Aktif:
              </span>
              {activeFilters.map(([k, v]) => {
            const field = fields.find(f => f.key === k);
            const label = field ? field.label : k;
            return <div key={k} className="flex items-center gap-1 bg-primary/10 border border-primary/30 text-primary px-2 py-1 rounded text-[10px] font-mono" data-id="element-249">
                    <span data-id="element-250">
                      {label}: {v}
                    </span>
                    <button onClick={() => removeFilter(k)} className="hover:text-primary-foreground hover:bg-primary rounded-full p-0.5 transition-colors" data-id="element-251">
                      <X className="w-3 h-3" data-id="element-252" />
                    </button>
                  </div>;
          })}
              <button onClick={onReset} className="text-[10px] font-mono text-destructive hover:underline ml-2" data-id="element-253">
                Bersihkan Semua
              </button>
            </> : <span className="text-[10px] font-mono text-muted-foreground" data-id="element-254">
              Tidak ada filter aktif. Menampilkan semua data.
            </span>}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto shrink-0" data-id="element-255">
          {resultCount !== undefined && <div className="text-[10px] font-mono text-muted-foreground mr-2 hidden sm:block" data-id="element-256">
              <span className="text-primary font-bold" data-id="element-257">{resultCount}</span>{' '}
              HASIL
            </div>}

          {exportData && exportColumns && exportFilename && <ExportDropdown data={exportData} columns={exportColumns} filename={exportFilename} title={`Ekspor ${exportFilename}`} data-id="element-258" />}

          <Button variant="outline" size="sm" onClick={onReset} className="h-8 text-xs font-mono border-border/50 hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30" data-id="element-259">
            <X className="w-3 h-3 mr-1.5" data-id="element-260" />
            Reset
          </Button>
          <Button size="sm" onClick={onApply} className="h-8 text-xs font-mono bg-primary/20 text-primary border border-primary/50 hover:bg-primary hover:text-primary-foreground" data-id="element-261">
            Terapkan Filter
          </Button>
        </div>
      </div>
    </div>;
}